import { useAuthenticatedContext } from "#src/contexts/AuthenticatedContext.helpers";
import { BaseError } from "@validereinc/utilities";
import { kebabCase } from "es-toolkit/compat";

/**
 * Get a storage key to use against browser storage mechanisms like local
 * storage for an authenticated user.
 */
export const getStorageKey = (
  /** the main unique identifier for the key (e.g. you can describe the UI location) */
  name: string | string[],
  /** what kind of key is this? */
  type: string,
  /** the authenticated user's ID */
  userId?: string,
  /** the authenticated user's company ID */
  companyId?: string
): string =>
  [
    type,
    userId,
    companyId,
    kebabCaseIfNot(
      Array.isArray(name) ? name.join("-").toLowerCase() : name.toLowerCase()
    ),
  ]
    .filter((c) => !!c)
    .join("-");

export const extractNameFromStorageKey = (
  storageKey: string,
  userId?: string,
  companyId?: string
): string => {
  if (!userId && !companyId) return storageKey.split("-").pop() ?? "";

  // Storage keys follow the format `${type}-${userId}-${companyId}-${name}`,
  // where `userId` and `companyId` are optional. Anchor on whichever provided
  // ID appears in the storage key (surrounded by hyphens to avoid coincidental
  // hex collisions inside another UUID), and take the substring after the
  // rightmost match so we skip past every ID present.
  let lastIdEnd = -1;

  for (const id of [userId, companyId]) {
    if (!id) continue;

    const anchoredId = `-${id}-`;
    const anchoredIdIndex = storageKey.indexOf(anchoredId);
    if (anchoredIdIndex === -1) continue;

    const endOfAnchoredId = anchoredIdIndex + anchoredId.length;
    if (endOfAnchoredId > lastIdEnd) lastIdEnd = endOfAnchoredId;
  }

  if (lastIdEnd === -1) return "";

  return storageKey.substring(lastIdEnd);
};

/**
 * Get a storage key to use against browser storage mechanisms like local
 * storage for an authenticated user.
 */
export const useStorageKey = (
  /** what kind of key is this? when an array is provided, one key is generated for each type using the name */
  type: string[],
  /** the main unique identifier for the key (e.g. you can describe the UI location). when an array is provided, they are strung together with kebab-casing. */
  name: string | string[]
): string[] => {
  const {
    v2: {
      userInfo: { user },
      companyInfo: { company },
    },
  } = useAuthenticatedContext();

  if (!type.length) throw new BaseError("Storage keys must have a type");
  if (!name.length) throw new BaseError("Storage key must have a name");

  return type.map((t) => getStorageKey(name, t, user?.id, company?.id));
};

function kebabCaseIfNot(val: string) {
  return !val.match(/^[a-z0-9]+(-[a-z0-9]+)*$/) ? kebabCase(val) : val;
}
